<script>
  import { get, call } from 'vuex-pathify'
  import ProductCard from '@/components/marketplace/ProductCard'
  import MarketModal from '@/components/marketplace/MarketModal'
  import MarketPromoModal from '@/components/marketplace/MarketPromoModal'
  import MarketPromoTab from '@/components/marketplace/MarketPromoTab'
  import marketPromoMixin from '@/mixins/marketPromoMixin'
  import screen from '@/helpers/screen'

  // Hardcoded fallbacks — used when Prismic document doesn't exist yet
  const FALLBACK_SLIDES = [
    {
      eyebrow: "Trending Editor's Picks",
      title: 'The Summer\nBreak Edit',
      bgColor: '#E18264',
      bgImage: null,
      imageOpacity: 0.7,
      gradient: null,
      ctaText: 'Shop Now',
      ctaLink: '/market/product-list/editors-picks',
    },
    {
      eyebrow: 'New Arrivals',
      title: 'Fresh Finds\nFor Fall',
      bgColor: '#325A50',
      bgImage: null,
      imageOpacity: 0.7,
      gradient: null,
      ctaText: 'Shop Now',
      ctaLink: '/market/product-list/editors-picks',
    },
    {
      eyebrow: 'Staff Picks',
      title: 'Curated\nWith Care',
      bgColor: '#889C4E',
      bgImage: null,
      imageOpacity: 0.7,
      gradient: null,
      ctaText: 'Shop Now',
      ctaLink: '/market/product-list/editors-picks',
    },
  ]

  const FALLBACK_SAVINGS = {
    title: 'Summer Savings',
    bgColor: '#325A50',
    left: {
      title: 'Pet toys &\nsupplies',
      subtitle: 'From Under',
      value: '$10',
      image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=800&q=80',
      link: '/market/product-list/pet-toys',
    },
    centerTop: {
      title: 'Travel friendly\nessentials',
      subtitle: 'Up To',
      value: '50% Off',
      image: 'https://images.unsplash.com/photo-1556228578-8c89e6adf883?w=800&q=80',
      link: '/market/product-list/travel-friendly',
    },
    centerBottom: {
      title: 'Sunglasses',
      subtitle: 'Up To',
      value: '50% Off',
      image: 'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=800&q=80',
      link: '/market/product-list/travel-friendly',
    },
    right: {
      title: 'New kitchen\nsupplies',
      subtitle: 'From Under',
      value: '$35',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80',
      link: '/market/product-list/kitchen-supplies',
    },
  }

  const FALLBACK_CATEGORIES = [
    {
      id: 'home-kitchen',
      title: 'Home & Kitchen',
      bgColor: '#EAB96D',
      image: null,
      link: '/market/product-list?categories=home-kitchen',
    },
    {
      id: 'beauty',
      title: 'Beauty & Personal Care',
      bgColor: '#EAB96D',
      image: null,
      link: '/market/product-list?categories=beauty',
    },
    {
      id: 'clothing',
      title: 'Clothing & Accessories',
      bgColor: '#E18264',
      image: null,
      link: '/market/product-list?categories=clothing',
    },
    {
      id: 'sports',
      title: 'Sports & Outdoors',
      bgColor: '#E18264',
      image: null,
      link: '/market/product-list?categories=sports',
    },
    {
      id: 'kids',
      title: 'Kids, Toys & Games',
      bgColor: '#CDAACD',
      image: null,
      link: '/market/product-list?categories=kids',
    },
    {
      id: 'pets',
      title: 'Pets',
      bgColor: '#F4EBD6',
      image: null,
      link: '/market/product-list?categories=pets',
    },
  ]

  const FALLBACK_PROMOS = [
    {
      heading: 'Cozy up with home decor',
      discountLabel: 'Up to',
      discountValue: '50',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=800&q=80',
      imageAlt: 'Cozy home decor',
      shopAllLink: '/market/product-list/home-decor',
      shopAllText: 'Shop All',
      productSlugs: [],
    },
    {
      heading: 'Eco-friendly cleaning essentials',
      discountLabel: 'Up to',
      discountValue: '50',
      image: 'https://images.unsplash.com/photo-1583947215259-38e31be8751f?w=800&q=80',
      imageAlt: 'Cleaning essentials',
      shopAllLink: '/market/product-list/eco-cleaning',
      shopAllText: 'Shop All',
      productSlugs: [],
    },
  ]

  // Convert a Prismic link field (full path or bare slug) to a Vue Router :to string.
  // Full paths like "/app/market/..." have the /app prefix stripped.
  // Bare slugs like "potlucks-picnics" are treated as collection slugs.
  function toRouterPath(value, fallbackSlug) {
    const raw = (typeof value === 'string' ? value : prismicText(value) || '').trim()
    if (raw.startsWith('/')) return raw.replace(/^\/app/, '')
    if (raw) return `/market/product-list/${raw}`
    return fallbackSlug ? `/market/product-list/${fallbackSlug}` : '/market/product-list'
  }

  const SAVINGS_CARD_GRADIENT =
    'linear-gradient(to bottom right, rgba(247, 239, 223, 0.88) 40%, rgba(247, 239, 223, 0) 75%)'

  // Helper: extract plain text from a Prismic rich text field or key text field
  function prismicText(field) {
    if (!field) return ''
    if (typeof field === 'string') return field
    if (Array.isArray(field) && field.length) return field[0].text || ''
    return ''
  }

  // Helper: build gradient CSS from a hex color
  function gradientFromHex(hex) {
    if (!hex) return null
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `linear-gradient(to bottom right, rgba(${r},${g},${b},0.95) 40%, rgba(${r},${g},${b},0) 75%)`
  }

  export default {
    components: {
      ProductCard,
      MarketModal,
      MarketPromoModal,
      MarketPromoTab,
    },
    mixins: [marketPromoMixin],
    metaInfo() {
      return {
        title: 'Alltrue Add-On Market — Up to 70% Off Retail',
        meta: [
          {
            vmid: 'description',
            name: 'description',
            content:
              "Shop Alltrue's Add-On Market and save up to 70% off retail. Curated home goods, beauty, wellness, pet supplies, kids' products, and more — no membership required.",
          },
          {
            vmid: 'og:title',
            property: 'og:title',
            content: 'Alltrue Add-On Market — Up to 70% Off Retail',
          },
          { vmid: 'og:url', property: 'og:url', content: 'https://shop.alltrue.com/app/market' },
          { vmid: 'og:type', property: 'og:type', content: 'website' },
        ],
        link: [{ rel: 'canonical', href: 'https://shop.alltrue.com/app/market' }],
        script: [
          {
            vmid: 'json-ld',
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org',
              '@type': 'WebSite',
              name: 'Alltrue Add-On Market',
              url: 'https://shop.alltrue.com/app/market',
              description:
                "Alltrue's curated marketplace — shop home goods, beauty, wellness, pets, and more at up to 70% off retail prices.",
              publisher: {
                '@type': 'Organization',
                name: 'Alltrue',
                url: 'https://shop.alltrue.com',
              },
            },
          },
        ],
      }
    },
    data() {
      return {
        activeSlide: 0,
        touchStartX: 0,
        savingsCardGradient: SAVINGS_CARD_GRADIENT,
        slideInterval: null,
      }
    },
    computed: {
      ...get('marketplace', ['categories', 'topTenProducts']),
      ...get('products', ['products']),
      ...get('config', ['marketplaceEnabled']),
      ...get('account', ['isAdmin']),
      ...get('account', { accountLoaded: 'loaded' }),
      ...get('prismic', ['marketLandingData']),
      screen,
      marketClosed() {
        if (!this.accountLoaded) return false
        return !this.marketplaceEnabled && !this.isAdmin
      },
      availableProducts() {
        return (this.products ?? []).filter((p) => !p.tags?.includes('sold-out'))
      },
      listingSections() {
        return this.promoBlocks.map((block, i) => {
          let products = []
          if (block.productSlugs?.length && this.products?.length) {
            const slugSet = new Set(block.productSlugs.map((s) => s.toUpperCase()))
            const bySlug = this.products.filter((p) => slugSet.has(p.slug?.toUpperCase()))
            const slugOrder = block.productSlugs.map((s) => s.toUpperCase())
            products = slugOrder
              .map((s) => bySlug.find((p) => p.slug?.toUpperCase() === s))
              .filter(Boolean)
          }
          if (!products.length) {
            products = this.availableProducts.slice(i * 6, i * 6 + 6)
          }
          const image = block.image || products[0]?.images?.[0] || null
          return { promo: { ...block, image }, products }
        })
      },

      // --- Prismic-driven sections with fallbacks ---
      // Data comes from Groups on the market_landing_page custom type (not slices).
      // Group fields live directly on marketLandingData (e.g. marketLandingData.hero_slides).

      heroSlides() {
        const items = this.marketLandingData?.hero_carousel
        if (items?.length) {
          return items.map((item) => {
            const bgColor = item.background_color || '#E18264'
            const bgImage = item.background_image?.url || null
            // Convert literal "\n" strings to real newlines for whitespace-pre-line rendering
            const title = prismicText(item.title).replace(/\\n/g, '\n')
            return {
              eyebrow: prismicText(item.eyebrow),
              title,
              bgColor,
              bgImage,
              imageOpacity: item.image_opacity ?? 0.7,
              gradient: bgImage ? gradientFromHex(bgColor) : null,
              ctaText: prismicText(item.cta_text) || 'Shop Now',
              ctaLink: toRouterPath(item.cta_collection, 'editors-picks'),
            }
          })
        }
        return FALLBACK_SLIDES
      },

      savingsData() {
        const d = this.marketLandingData
        if (!d?.savings_cards?.length) return FALLBACK_SAVINGS

        const cards = d.savings_cards
        const cardByPosition = (pos) => cards.find((c) => c.card_position === pos)

        const mapCard = (card, fallback) => {
          if (!card) return fallback
          return {
            title: (prismicText(card.card_title) || fallback.title).replace(/\\n/g, '\n'),
            subtitle: prismicText(card.card_subtitle) || fallback.subtitle,
            value: prismicText(card.card_value) || fallback.value,
            image: card.card_image?.url || fallback.image,
            link: toRouterPath(card.card_collection, null) || fallback.link,
          }
        }

        return {
          title: prismicText(d.savings_title) || FALLBACK_SAVINGS.title,
          bgColor: d.savings_background_color || FALLBACK_SAVINGS.bgColor,
          left: mapCard(cardByPosition('left'), FALLBACK_SAVINGS.left),
          centerTop: mapCard(cardByPosition('center_top'), FALLBACK_SAVINGS.centerTop),
          centerBottom: mapCard(cardByPosition('center_bottom'), FALLBACK_SAVINGS.centerBottom),
          right: mapCard(cardByPosition('right'), FALLBACK_SAVINGS.right),
        }
      },

      categorySectionTitle() {
        return prismicText(this.marketLandingData?.categories_title) || 'Seasonal Must-haves'
      },

      displayCategories() {
        // Try Prismic category overrides first
        const items = this.marketLandingData?.category_items
        if (items?.length) {
          return items.map((item) => ({
            id: item.collection_slug || '',
            title: prismicText(item.title),
            bgColor: item.background_color || '#EAB96D',
            image: item.image?.url || null,
            link: toRouterPath(item.item_link || item.collection_slug, null),
          }))
        }
        // Fall back to API categories (add default link), then static
        if (this.categories?.length) {
          return this.categories.map((c) => ({
            ...c,
            link: c.link || `/market/product-list?categories=${c.id}`,
          }))
        }
        return FALLBACK_CATEGORIES
      },

      promoBlocks() {
        const slices = (this.marketLandingData?.body || []).filter(
          (s) => s.slice_type === 'listing_section_block_'
        )
        if (slices.length) {
          return slices.map((slice, i) => {
            const p = slice.primary || {}
            const fallback = FALLBACK_PROMOS[i] || FALLBACK_PROMOS[0]
            const rawLink = prismicText(p.shop_all_link) || p.shop_all_link || ''
            const shopAllLink = rawLink ? rawLink.replace(/^\/app/, '') : fallback.shopAllLink
            return {
              heading: prismicText(p.heading) || fallback.heading,
              discountLabel: prismicText(p.discount_label) || fallback.discountLabel,
              discountValue: prismicText(p.discount_value) || fallback.discountValue,
              image: p.background_image?.url || null,
              imageAlt: prismicText(p.heading) || fallback.imageAlt,
              shopAllLink,
              shopAllText: prismicText(p.shop_all_text) || fallback.shopAllText,
              productSlugs: (slice.items || [])
                .map((item) => item.product_slug)
                .filter(Boolean),
            }
          })
        }
        return FALLBACK_PROMOS
      },

      favoritesTitle() {
        return (
          prismicText(this.marketLandingData?.favorites_title) ||
          'Stock up on these all-time favorites'
        )
      },
    },
    async created() {
      this.$store.dispatch('marketplace/ensureFresh')
      this.$store.dispatch('products/ensureFresh')
      await this.$store.dispatch('prismic/loadMarketLandingData')

      if (this.promoEnabled && this.$route.query.post_customization === '1') {
        localStorage.setItem('alltrue_promo_eligible', 'true')
        // POSTHOG_PLACEHOLDER: replace 'a' with PostHog.getFeatureFlag('market-promo-variant') for A/B test
        const variant = 'a'
        localStorage.setItem('alltrue_promo_variant', variant)
        this.isPromoEligible = true
        this.promoVariant = variant
        this.$router.replace({
          ...this.$route,
          query: { ...this.$route.query, post_customization: undefined },
        })
        if (!this.isPromoSuppressed()) {
          this.showPromoModal = true
        }
      }
    },
    mounted() {
      this.startAutoSlide()
    },
    beforeDestroy() {
      clearInterval(this.slideInterval)
    },
    methods: {
      ...call('marketplaceCart', ['addToCart', 'addToWaitlist']),
      getCategoryImage(category) {
        if (category.image) return category.image
        const match = FALLBACK_CATEGORIES.find(
          (s) =>
            s.id === category.id ||
            s.id === category.slug ||
            s.title.toLowerCase() === category.title?.toLowerCase()
        )
        return match ? match.image : null
      },
      getCategoryBgColor(category, index) {
        if (category.bgColor) return category.bgColor
        const match = FALLBACK_CATEGORIES.find(
          (s) =>
            s.id === category.id ||
            s.id === category.slug ||
            s.title.toLowerCase() === category.title?.toLowerCase()
        )
        if (match?.bgColor) return match.bgColor
        const palette = ['#EAB96D', '#EAB96D', '#E18264', '#E18264', '#CDAACD', '#F4EBD6']
        return palette[index % palette.length]
      },
      filterByCategory(category) {
        const apiMatch = this.categories.find(
          (c) => c.slug === category.id || c.title?.toLowerCase() === category.title?.toLowerCase()
        )
        const filterId = apiMatch ? String(apiMatch.id) : category.id
        this.$store.set('marketplace/filters', { categories: [filterId] })
        this.$navigate({ name: 'MarketProductList' })
      },
      pauseAutoSlide() {
        clearInterval(this.slideInterval)
      },
      startAutoSlide() {
        clearInterval(this.slideInterval)
        this.slideInterval = setInterval(() => {
          this.activeSlide = (this.activeSlide + 1) % this.heroSlides.length
        }, 6000)
      },
      prevSlide() {
        this.activeSlide = (this.activeSlide - 1 + this.heroSlides.length) % this.heroSlides.length
        this.startAutoSlide()
      },
      nextSlide() {
        this.activeSlide = (this.activeSlide + 1) % this.heroSlides.length
        this.startAutoSlide()
      },
      onTouchStart(e) {
        this.touchStartX = e.touches[0].clientX
      },
      onTouchEnd(e) {
        const delta = this.touchStartX - e.changedTouches[0].clientX
        if (Math.abs(delta) > 40) {
          if (delta > 0) {
            this.nextSlide()
          } else {
            this.prevSlide()
          }
        }
      },
    },
  }
</script>

<template>
  <div class="bg-dawn">
    <!-- Hero Carousel -->
    <div data-cy="market-landing-hero" class="bg-dawn px-4 md:px-6 pt-4 md:pt-6 pb-4 md:pb-6">
      <div class="max-w-320 mx-auto">
        <div
          class="relative rounded-md overflow-hidden"
          style="min-height: 280px"
          @touchstart="onTouchStart"
          @touchend="onTouchEnd"
          @mouseenter="pauseAutoSlide"
          @mouseleave="startAutoSlide"
        >
          <!-- Slide layers — all stacked, only active one is visible -->
          <transition name="hero-fade" mode="out-in">
            <div
              :key="activeSlide"
              class="absolute inset-0"
              :style="{ backgroundColor: heroSlides[activeSlide].bgColor }"
            >
              <!-- Background image -->
              <div
                v-if="heroSlides[activeSlide].bgImage"
                class="absolute inset-0 bg-cover bg-center"
                :style="{
                  backgroundImage: `url(${heroSlides[activeSlide].bgImage})`,
                  opacity: heroSlides[activeSlide].imageOpacity,
                }"
              />
              <!-- Gradient overlay -->
              <div
                v-if="heroSlides[activeSlide].gradient"
                class="absolute inset-0"
                :style="{ background: heroSlides[activeSlide].gradient }"
              />

              <!-- Slide content -->
              <div class="relative z-10 px-8 md:px-10 pt-8 md:pt-10 pb-8 max-w-lg hero-content">
                <p
                  class="text-dawn-lt3 text-base md:text-25px uppercase tracking-widest mb-2 font-normal"
                >
                  {{ heroSlides[activeSlide].eyebrow }}
                </p>
                <h1
                  class="font-heading font-semibold text-30px md:text-45px text-white mb-5 whitespace-pre-line"
                  style="line-height: 1.05"
                  >{{ heroSlides[activeSlide].title }}</h1
                >
                <RouterLink
                  :to="heroSlides[activeSlide].ctaLink"
                  class="inline-flex items-center px-5 py-2 rounded-full bg-dawn-lt3 text-black text-sm font-normal hover:bg-dawn transition"
                >
                  {{ heroSlides[activeSlide].ctaText }}
                </RouterLink>
              </div>
            </div>
          </transition>

          <!-- Navigation arrows: top right -->
          <div class="absolute top-4 right-4 z-20 flex gap-2">
            <button
              type="button"
              class="w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition"
              style="background-color: #f7efdf"
              aria-label="Previous slide"
              @click="prevSlide"
            >
              <BaseIcon :size="4" class="text-black">
                <IconChevronLeft />
              </BaseIcon>
            </button>
            <button
              type="button"
              class="w-8 h-8 rounded-full flex items-center justify-center hover:opacity-80 transition"
              style="background-color: #f7efdf"
              aria-label="Next slide"
              @click="nextSlide"
            >
              <BaseIcon :size="4" class="text-black">
                <IconChevronRight />
              </BaseIcon>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Summer Savings Section -->
    <div class="px-4 md:px-6 py-12 md:py-16" :style="{ backgroundColor: savingsData.bgColor }">
      <div class="max-w-320 mx-auto">
        <h2 class="text-center font-heading font-medium text-30px md:text-8xl mb-8 text-white">{{
          savingsData.title
        }}</h2>

        <!-- Desktop: 3-col grid (wide | narrow | wide). Mobile: stacked. -->
        <div
          class="hidden md:grid gap-4"
          style="grid-template-columns: 1fr 0.8fr 1fr; min-height: 680px"
        >
          <!-- Left card (text top → gradient fades down) -->
          <RouterLink
            :to="savingsData.left.link"
            class="rounded-md overflow-hidden relative hover:opacity-95 transition bg-cover bg-bottom"
            :style="{
              backgroundColor: '#F7EFDF',
              backgroundImage: `url(${savingsData.left.image})`,
            }"
          >
            <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
            <div class="relative z-10 p-8 pt-10">
              <h3
                class="font-heading font-semibold text-8xl leading-tight mb-8 text-black whitespace-pre-line"
              >
                {{ savingsData.left.title }}
              </h3>
              <p class="text-sm font-normal mb-1 text-black">{{ savingsData.left.subtitle }}</p>
              <p class="font-heading leading-none" style="font-size: 5rem; color: #e18264">{{
                savingsData.left.value
              }}</p>
            </div>
          </RouterLink>

          <!-- Middle: 2 stacked full-bleed photo cards, text at top → gradient fades down -->
          <div class="flex flex-col gap-4">
            <RouterLink
              :to="savingsData.centerTop.link"
              class="rounded-md overflow-hidden flex-1 relative hover:opacity-95 transition bg-cover bg-bottom"
              :style="{
                backgroundColor: '#F7EFDF',
                backgroundImage: `url(${savingsData.centerTop.image})`,
              }"
            >
              <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
              <div class="relative z-10 p-5 pt-6">
                <h3
                  class="font-heading font-semibold text-3xl leading-tight mb-1 text-black whitespace-pre-line"
                >
                  {{ savingsData.centerTop.title }}
                </h3>
                <p class="text-xs mb-1 text-black">{{ savingsData.centerTop.subtitle }}</p>
                <p class="font-heading text-3xl leading-none" style="color: #e18264">{{
                  savingsData.centerTop.value
                }}</p>
              </div>
            </RouterLink>
            <RouterLink
              :to="savingsData.centerBottom.link"
              class="rounded-md overflow-hidden flex-1 relative hover:opacity-95 transition bg-cover bg-bottom"
              :style="{
                backgroundColor: '#F7EFDF',
                backgroundImage: `url(${savingsData.centerBottom.image})`,
              }"
            >
              <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
              <div class="relative z-10 p-5 pt-6">
                <h3
                  class="font-heading font-semibold text-3xl leading-tight mb-1 text-black whitespace-pre-line"
                  >{{ savingsData.centerBottom.title }}</h3
                >
                <p class="text-xs mb-1 text-black">{{ savingsData.centerBottom.subtitle }}</p>
                <p class="font-heading text-3xl leading-none" style="color: #e18264">{{
                  savingsData.centerBottom.value
                }}</p>
              </div>
            </RouterLink>
          </div>

          <!-- Right card (text top → gradient fades down) -->
          <RouterLink
            :to="savingsData.right.link"
            class="rounded-md overflow-hidden relative hover:opacity-95 transition bg-cover bg-bottom"
            :style="{
              backgroundColor: '#F7EFDF',
              backgroundImage: `url(${savingsData.right.image})`,
            }"
          >
            <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
            <div class="relative z-10 p-8 pt-10">
              <h3
                class="font-heading font-semibold text-8xl leading-tight mb-8 text-black whitespace-pre-line"
              >
                {{ savingsData.right.title }}
              </h3>
              <p class="text-sm font-normal mb-1 text-black">{{ savingsData.right.subtitle }}</p>
              <p class="font-heading leading-none" style="font-size: 5rem; color: #e18264">{{
                savingsData.right.value
              }}</p>
            </div>
          </RouterLink>
        </div>

        <!-- Mobile: stacked layout -->
        <div class="md:hidden flex flex-col gap-4">
          <!-- Left card -->
          <RouterLink
            :to="savingsData.left.link"
            class="rounded-md overflow-hidden relative hover:opacity-95 transition bg-cover bg-bottom"
            :style="{
              backgroundColor: '#F7EFDF',
              backgroundImage: `url(${savingsData.left.image})`,
              minHeight: '240px',
            }"
          >
            <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
            <div class="relative z-10 p-6">
              <h3
                class="font-heading font-semibold text-30px leading-tight mb-4 text-black whitespace-pre-line"
              >
                {{ savingsData.left.title }}
              </h3>
              <p class="text-sm font-normal mb-1 text-black">{{ savingsData.left.subtitle }}</p>
              <p class="font-heading leading-none" style="font-size: 4rem; color: #e18264">{{
                savingsData.left.value
              }}</p>
            </div>
          </RouterLink>

          <!-- Center cards side by side -->
          <div class="flex gap-4">
            <RouterLink
              :to="savingsData.centerTop.link"
              class="rounded-md overflow-hidden flex-1 relative hover:opacity-95 transition bg-cover bg-bottom"
              :style="{
                backgroundColor: '#F7EFDF',
                backgroundImage: `url(${savingsData.centerTop.image})`,
                minHeight: '240px',
              }"
            >
              <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
              <div class="relative z-10 p-4 pt-5">
                <h3
                  class="font-heading font-semibold text-xl leading-tight mb-1 text-black whitespace-pre-line"
                >
                  {{ savingsData.centerTop.title }}
                </h3>
                <p class="text-xs mb-1 text-black">{{ savingsData.centerTop.subtitle }}</p>
                <p class="font-heading text-2xl leading-none" style="color: #e18264">{{
                  savingsData.centerTop.value
                }}</p>
              </div>
            </RouterLink>
            <RouterLink
              :to="savingsData.centerBottom.link"
              class="rounded-md overflow-hidden flex-1 relative hover:opacity-95 transition bg-cover bg-bottom"
              :style="{
                backgroundColor: '#F7EFDF',
                backgroundImage: `url(${savingsData.centerBottom.image})`,
                minHeight: '240px',
              }"
            >
              <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
              <div class="relative z-10 p-4 pt-5">
                <h3
                  class="font-heading font-semibold text-xl leading-tight mb-1 text-black whitespace-pre-line"
                  >{{ savingsData.centerBottom.title }}</h3
                >
                <p class="text-xs mb-1 text-black">{{ savingsData.centerBottom.subtitle }}</p>
                <p class="font-heading text-2xl leading-none" style="color: #e18264">{{
                  savingsData.centerBottom.value
                }}</p>
              </div>
            </RouterLink>
          </div>

          <!-- Right card -->
          <RouterLink
            :to="savingsData.right.link"
            class="rounded-md overflow-hidden relative hover:opacity-95 transition bg-cover bg-bottom"
            :style="{
              backgroundColor: '#F7EFDF',
              backgroundImage: `url(${savingsData.right.image})`,
              minHeight: '240px',
            }"
          >
            <div class="absolute inset-0" :style="{ background: savingsCardGradient }" />
            <div class="relative z-10 p-6">
              <h3
                class="font-heading font-semibold text-30px leading-tight mb-4 text-black whitespace-pre-line"
              >
                {{ savingsData.right.title }}
              </h3>
              <p class="text-sm font-normal mb-1 text-black">{{ savingsData.right.subtitle }}</p>
              <p class="font-heading leading-none" style="font-size: 4rem; color: #e18264">{{
                savingsData.right.value
              }}</p>
            </div>
          </RouterLink>
        </div>
      </div>
    </div>

    <!-- Seasonal Must-haves Section -->
    <div data-cy="market-landing-categories" class="bg-dawn-lt2 px-4 md:px-6 py-12 md:py-16">
      <div class="max-w-320 mx-auto">
        <h2 class="text-center font-heading font-semibold text-30px md:text-8xl mb-10 text-black">{{
          categorySectionTitle
        }}</h2>
        <div class="flex flex-wrap justify-center gap-6">
          <RouterLink
            v-for="(category, index) in displayCategories"
            :key="category.id"
            :to="category.link"
            class="flex flex-col items-center group"
          >
            <div
              class="w-25 h-25 md:w-48 md:h-48 rounded-full mb-3 group-hover:opacity-90 transition overflow-hidden flex items-center justify-center"
              :style="{ backgroundColor: getCategoryBgColor(category, index) }"
            >
              <img
                v-if="getCategoryImage(category)"
                :src="getCategoryImage(category)"
                :alt="category.title"
                class="w-full h-full object-cover rounded-full"
              />
              <span v-else class="text-white text-2xl font-bold">
                {{ category.title.charAt(0) }}
              </span>
            </div>
            <span
              class="text-3xs md:text-xl text-center font-normal text-black"
              style="max-width: 120px"
            >
              {{ category.title }}
            </span>
          </RouterLink>
        </div>
      </div>
    </div>

    <!-- All-time Favorites Section -->
    <div v-if="topTenProducts && topTenProducts.length" class="bg-fall px-4 md:px-6 py-8 md:py-16">
      <div class="max-w-320 mx-auto">
        <h2
          class="text-center font-heading font-semibold text-30px md:text-8xl mb-6 md:mb-10 text-black"
        >
          {{ favoritesTitle }}
        </h2>

        <!-- Listing Section Blocks -->
        <div
          v-for="(section, sectionIndex) in listingSections"
          :key="sectionIndex"
          :class="{ 'mb-12': sectionIndex < listingSections.length - 1 }"
        >
          <div
            class="flex flex-col gap-5 md:gap-8"
            :class="sectionIndex % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'"
          >
            <!-- Promo card -->
            <RouterLink
              :to="section.promo.shopAllLink"
              class="md:w-5/12 flex-shrink-0 bg-dawn overflow-hidden flex flex-row md:flex-col hover:opacity-95 transition h-50 md:h-auto"
              style="border-radius: 15px"
            >
              <div class="w-1/2 md:w-auto flex-shrink-0 flex flex-col justify-between p-5 md:p-10">
                <h3
                  class="font-heading font-semibold text-xl md:text-45px text-black leading-tight"
                >
                  {{ section.promo.heading }}
                </h3>
                <div>
                  <p class="text-3xs md:text-25px font-normal text-black mb-1">{{
                    section.promo.discountLabel
                  }}</p>
                  <div class="flex items-end gap-2 md:gap-3">
                    <span
                      class="font-heading font-semibold text-summer leading-none"
                      :style="
                        screen.md
                          ? 'font-size: 6.5rem; line-height: 0.96'
                          : 'font-size: 3rem; line-height: 0.96'
                      "
                      >{{ section.promo.discountValue }}</span
                    >
                    <div
                      class="font-heading font-semibold text-summer flex flex-col leading-none text-xxl md:text-5xl pb-1"
                    >
                      <span>%</span>
                      <span>off</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="w-1/2 md:w-auto md:flex-1 md:min-h-48 overflow-hidden">
                <img
                  v-if="section.promo.image"
                  :src="section.promo.image"
                  :alt="section.promo.imageAlt"
                  class="w-full h-full object-cover"
                />
              </div>
            </RouterLink>
            <!-- Product grid -->
            <div class="flex-1">
              <div class="mb-2 md:mb-6">
                <RouterLink
                  :to="section.promo.shopAllLink"
                  class="text-base md:text-25px font-normal text-black underline hover:opacity-70 transition"
                  >{{ section.promo.shopAllText }}</RouterLink
                >
              </div>
              <div class="grid grid-cols-2 md:grid-cols-3 gap-x-3 gap-y-5 md:gap-y-8">
                <ProductCard
                  v-for="product in section.products"
                  :key="product.id"
                  :product="product"
                  :to="{
                    name: 'MarketLandingProduct',
                    params: {
                      productId: product.id,
                      productSlug: product.slug,
                      productTitle: product.title,
                    },
                  }"
                  type="marketplace"
                  @viewDetails="
                    $navigate({
                      name: 'MarketLandingProduct',
                      params: {
                        productId: product.id,
                        productSlug: product.slug,
                        productTitle: product.title,
                      },
                    })
                  "
                  @addToCart="addToCart({ variantId: product.defaultVariant.id })"
                  @addToWaitlist="addToWaitlist({ productId: product.id })"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Market Closed Modal -->
    <Portal v-if="marketClosed" to="modal">
      <MarketModal>
        <p>
          The Add-On Market is now closed! Please stay tuned to your Account page for shipping
          updates!
        </p>
      </MarketModal>
    </Portal>

    <Portal to="productModal">
      <RouterView />
    </Portal>

    <!-- Post-customization promo modal (recurring subscribers only) -->
    <MarketPromoModal
      v-if="showPromoModal"
      :open="showPromoModal"
      :headline="promoHeadline"
      :subhead="promoSubhead"
      :cta-text="promoCtaText"
      :cta-url="promoUrl"
      :dismiss-text="promoDismissText"
      @dismiss="handlePromoDismiss"
    />

    <!-- Persistent promo tab — visible to eligible recurring subscribers on market pages -->
    <MarketPromoTab v-if="isPromoEligible" :label="promoTabLabel" @click="openPromo" />
  </div>
</template>

<style scoped>
  /* Hero carousel crossfade with subtle content lift */
  .hero-fade-enter-active {
    transition: opacity 0.6s ease;
  }

  .hero-fade-enter-active .hero-content {
    transition: opacity 0.6s ease, transform 0.6s ease;
  }

  .hero-fade-leave-active {
    transition: opacity 0.4s ease;
  }

  .hero-fade-enter {
    opacity: 0;
  }

  .hero-fade-enter .hero-content {
    opacity: 0;
    transform: translateY(12px);
  }

  .hero-fade-leave-to {
    opacity: 0;
  }
</style>
